import { Lock, MapPin, KeyRound, ShieldCheck } from "lucide-react";
import SituationPage from "@/components/site/SituationPage";
import { useMeta } from "@/hooks/useMeta";

/* A sealed vault entry, the record stays sealed; release requires a legal order or signed release + the user's key */
function SealedRecordMock() {
  return (
    <div className="mx-auto lg:mx-0 max-w-sm rounded-2xl border border-[color:var(--yn-border)] bg-white p-5 sm:p-6 shadow-sm">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <ShieldCheck size={16} style={{ color: "var(--yn-primary)" }} />
          <span className="text-xs font-semibold uppercase tracking-wider text-[color:var(--yn-primary)]">
            Sealed record
          </span>
        </div>
        <span className="inline-flex items-center gap-1 text-xs font-medium text-[color:var(--yn-ink)]/70">
          <Lock size={13} />
          Sealed vault
        </span>
      </div>

      <dl className="mt-5 space-y-3">
        <div>
          <dt className="text-[11px] uppercase tracking-wider text-[color:var(--yn-muted)]">
            Captured
          </dt>
          <dd className="mt-0.5 text-sm font-medium text-[color:var(--yn-ink)]">
            March 4, 2026 · 2:14 PM
          </dd>
        </div>
        <div>
          <dt className="text-[11px] uppercase tracking-wider text-[color:var(--yn-muted)]">
            Location
          </dt>
          <dd className="mt-0.5 text-sm font-medium text-[color:var(--yn-ink)]">
            33.3062° N, 111.8413° W
          </dd>
        </div>
        <div>
          <dt className="text-[11px] uppercase tracking-wider text-[color:var(--yn-muted)]">
            SHA-256 fingerprint
          </dt>
          <dd className="mt-0.5 text-xs font-mono text-[color:var(--yn-ink)] break-all">
            9f2b7a3c8d4e6f10a7b3c8d4e6f10a7b3c8d4e6f10a7b3c8d4e6f10a7b3c8d4e
          </dd>
        </div>
      </dl>

      <div className="mt-5 pt-4 border-t border-[color:var(--yn-border)]">
        <p className="text-[11px] text-[color:var(--yn-muted)] leading-relaxed">
          Released only by legal order or signed release along with your
          encryption/decryption key. YeboNadi decrypts and delivers the file
          via secure cloud storage. A retrieval fee applies.
        </p>
      </div>
    </div>
  );
}

export default function Accused() {
  useMeta({
    title: "How to prove a false accusation, YeboNadi",
    description:
      "When someone tells the wrong story first and loudest, a sealed, time-stamped, GPS-locked recording moves the fight from who sounds believable to what can be verified. YeboNadi seals the moment at capture.",
    path: "/accused",
  });

  return (
    <SituationPage
      slug="accused"
      kicker="Falsely accused"
      headline="Their story is getting better. Yours is sealed."
      narrative={[
        "It starts as something small, a conversation at work, a doorstep argument, a dispute over money or space. You know what happened. You were there.",
        "Then someone else tells it first. Louder. To the wrong people. And every time they tell it, the story gets a little sharper. By the time it reaches HR, or the landlord, or an officer, it is not the incident anymore, it is the retelling. And you are trying to remember exact words from a moment you did not know you'd have to defend.",
        "A sealed record moves the conversation. It stops being about who sounds more believable and starts being about what can actually be verified, the audio, the time, the place. That is the fight you can win.",
      ]}
      miniDemo={<SealedRecordMock />}
      coversHeading="How YeboNadi covers this"
      covers={[
        {
          icon: Lock,
          title: "Sealed at capture, not editable after the fact",
          body: "Each recording is encrypted (AES-256) and hash-sealed (SHA-256) at the moment of capture, so no one, including you, can quietly change what was said.",
        },
        {
          icon: MapPin,
          title: "Time and location, embedded",
          body: "The exact timestamp and GPS location are written into the sealed record itself. Anyone reviewing it later sees where and when the capture happened.",
        },
        {
          icon: KeyRound,
          title: "Released only by legal order or signed release",
          body: "Nobody, including you, can play a sealed recording inside the app. When it's needed in a proceeding or for your use, your lawyer, the court, or an authorized party submits a legal order or a signed release, YeboNadi decrypts the file and delivers it via secure cloud storage. A retrieval fee applies.",
        },
        {
          icon: ShieldCheck,
          title: "Survives your phone",
          body: "Sealed recordings are backed up to encrypted cloud storage, so even if the phone is lost, broken, or taken, the record is still there when you need it.",
        },
      ]}
      subsectionTitle="When the story becomes a proceeding."
      subsectionBody={
        <>
          <p>
            A sealed recording isn&apos;t something you hand over on demand ,
            and that&apos;s the point. Nobody can quietly edit it, pressure
            you to release it, or dispute what it says without your
            permission.
          </p>
          <p>
            When it becomes evidence in a case, a claim, or a defense, your
            representative, your lawyer, the court, or an authorized party
            submits a legal order or a signed release. YeboNadi decrypts the
            record from your sealed vault and delivers it via secure cloud
            storage. A retrieval fee applies.
          </p>
          <p>
            The file that reaches the requestor comes with the capture
            timestamp and the capture location, but this must be accompanied
            by your encryption/decryption key.
          </p>
        </>
      }
    />
  );
}
